import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import type { UserProfile } from '../types'
import { searchableUserMenuStyle } from '../searchableUserMenu'
import { Icon } from './Icon'
import { UserAvatar } from './UserAvatar'

export interface UserSelectOption {
  value: string
  name: string
  profile: UserProfile
  email?: string
  picture?: string
}

interface Props {
  value: string
  options: UserSelectOption[]
  onChange: (value: string) => void
  name?: string
  disabled?: boolean
  ariaLabel: string
  title?: string
  placeholder?: string
  searchPlaceholder?: string
}

function normalized(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

export function SearchableUserSelect({ value, options, onChange, name, disabled = false, ariaLabel, title, placeholder = 'Selecione um responsável', searchPlaceholder = 'Pesquisar por nome, perfil ou e-mail' }: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({})
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = useId()
  const selected = options.find(option => option.value === value)
  const filtered = options.filter(option => normalized(`${option.name} ${option.profile} ${option.email ?? ''} ${option.value}`).includes(normalized(query.trim())))

  useEffect(() => {
    if (!open) return

    function positionMenu() {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (!rect) return
      const bodyZoom = Number.parseFloat(window.getComputedStyle(document.body).zoom) || 1
      setMenuStyle(searchableUserMenuStyle(rect, { width: window.innerWidth, height: window.innerHeight }, bodyZoom))
    }

    function closeOutside(event: PointerEvent) {
      if (!(event.target instanceof Node)) return
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false)
    }

    function closeWithEscape(event: globalThis.KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }

    positionMenu()
    requestAnimationFrame(() => inputRef.current?.focus())
    document.addEventListener('pointerdown', closeOutside)
    document.addEventListener('keydown', closeWithEscape)
    window.addEventListener('resize', positionMenu)
    window.addEventListener('scroll', positionMenu, true)
    return () => {
      document.removeEventListener('pointerdown', closeOutside)
      document.removeEventListener('keydown', closeWithEscape)
      window.removeEventListener('resize', positionMenu)
      window.removeEventListener('scroll', positionMenu, true)
    }
  }, [open])

  function toggle() {
    if (disabled) return
    setQuery('')
    setOpen(current => !current)
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      if (!open) toggle()
    }
  }

  function select(option: UserSelectOption) {
    onChange(option.value)
    setOpen(false)
    setQuery('')
    triggerRef.current?.focus()
  }

  return <div className={`searchable-user-select ${disabled ? 'disabled' : ''}`}>
    {name && <input type="hidden" name={name} value={value}/>}
    <button ref={triggerRef} type="button" className="searchable-user-trigger" disabled={disabled} aria-label={ariaLabel} aria-haspopup="listbox" aria-controls={listboxId} aria-expanded={open} title={title} onClick={toggle} onKeyDown={handleTriggerKeyDown}>
      <span className="searchable-user-selection">{selected ? <><UserAvatar initials={selected.value} name={selected.name} picture={selected.picture} className="searchable-user-avatar"/><span><b>{selected.name}</b><small>{selected.profile}</small></span></> : <b>{placeholder}</b>}</span><span className="searchable-user-chevron" aria-hidden="true">⌄</span>
    </button>
    {open && createPortal(<div ref={menuRef} className="searchable-user-menu" style={menuStyle}>
      <label className="searchable-user-search"><Icon name="search"/><span className="sr-only">Pesquisar usuário</span><input ref={inputRef} value={query} onChange={event => setQuery(event.target.value)} placeholder={searchPlaceholder} aria-label="Pesquisar usuário"/></label>
      <div id={listboxId} className="searchable-user-options" role="listbox" aria-label={ariaLabel}>
        {filtered.map(option => <button type="button" role="option" aria-selected={option.value === value} className={option.value === value ? 'selected' : ''} key={`${option.email ?? option.value}-${option.value}`} onClick={() => select(option)}><UserAvatar initials={option.value} name={option.name} picture={option.picture} className="searchable-user-avatar"/><span><b>{option.name}</b><small>{option.profile}{option.email ? ` · ${option.email}` : ''}</small></span>{option.value === value && <i aria-hidden="true">✓</i>}</button>)}
        {!filtered.length && <div className="searchable-user-empty"><b>Nenhum usuário encontrado</b><span>Tente pesquisar outro nome ou perfil.</span></div>}
      </div>
    </div>, document.body)}
  </div>
}
